// Region filter options for the report/registry lists. These live in
// rv2_org_config (admin-editable) but aren't part of the public /communities
// projection, so they come off the portal org-config endpoint rather than
// getServiceConfig(), which still serves the hardcoded map as a fallback.
import { useContext } from "react";

import { LabelValue, getOrgConfigPortal } from "@rubi/api-generated";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";

import { getServiceConfig } from "@/common/serviceConfigurations";
import { AuthStateContext } from "@/contexts/AuthContext";

export function useRegionFilterOptions(): {
  options: LabelValue[];
  isLoading: boolean;
} {
  const { currentUser } = useContext(AuthStateContext);
  const { orgId } = useParams();
  const resolvedOrgId = orgId ?? currentUser?.rubi_org_id ?? "";

  const { data, isLoading } = useQuery({
    queryKey: ["org-config-region-filter-options", resolvedOrgId],
    queryFn: async () =>
      (await getOrgConfigPortal({ path: { org_id: resolvedOrgId } })).data
        ?.metadata?.region_filter_options ?? null,
    enabled: resolvedOrgId !== "",
    staleTime: 60 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // An absent value (unset in the DB, or not loaded yet) falls back to the
  // hardcoded map; an explicitly empty list is honoured, so an admin can clear
  // the filter.
  return {
    options: data ?? getServiceConfig(resolvedOrgId)?.regionFilterOptions ?? [],
    // `enabled: false` leaves react-query's isLoading permanently true, so
    // only report loading when a fetch can actually happen.
    isLoading: resolvedOrgId !== "" && isLoading,
  };
}
